import { useEffect } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { editorApiBase } from "@/lib/editorApi";

/**
 * Returns live-editable content when in edit mode, or the static default otherwise.
 *
 * @param contentId - Content path, e.g. "pages/home" or "retreats/art-of-presence"
 * @param staticDefault - The statically-imported content object (used in production and as initial value)
 */
export function useContent<T>(contentId: string, staticDefault: T, options?: { setActive?: boolean }): T {
  const setActive = options?.setActive ?? true;
  const { editMode, overrides, updateContent, setActiveContentId } = useEditMode();

  // Set this page's contentId as active for the sidebar
  useEffect(() => {
    if (editMode && setActive) {
      setActiveContentId(contentId);
      return () => setActiveContentId(null);
    }
  }, [editMode, setActive, contentId, setActiveContentId]);

  // On first entering edit mode, fetch fresh content from disk
  useEffect(() => {
    if (!editMode) return;
    if (overrides[contentId]) return; // already loaded

    const [type, ...rest] = contentId.split("/");
    const id = rest.join("/");

    fetch(`${editorApiBase()}/content/${type}/${id}`)
      .then((r) => r.json())
      .then((data) => updateContent(contentId, data))
      .catch(() => {
        // Fall back to static default
        updateContent(contentId, staticDefault);
      });
  }, [editMode, contentId, overrides, updateContent, staticDefault]);

  if (editMode && overrides[contentId]) {
    return overrides[contentId] as T;
  }

  return staticDefault;
}
